import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import AppsIcon from '@material-ui/icons/Apps';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import CloudIcon from '@material-ui/icons/Cloud';
import OfflineBoltIcon from '@material-ui/icons/OfflineBolt';
import AddTaskIcon from '@material-ui/icons/PlaylistAdd';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import SettingsApplications from '@material-ui/icons/SettingsApplications';

import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { usePermission } from '@backstage/plugin-permission-react';
import { moduleAdminsViewPermission } from '../../policies/policies';

const useSidebarLogoStyles = makeStyles(theme => ({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    paddingBottom: theme.spacing(1),
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
    display: 'flex',
    alignItems: 'center',
  },
}));

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const { allowed: isAdminsViewAllowed } = usePermission({ permission: moduleAdminsViewPermission });

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Discover" icon={<MenuIcon />}>
          <SidebarItem icon={HomeIcon} to="home" text="Home" />
          <SidebarItem icon={AppsIcon} to="catalog" text="Catalog" />
          <MyGroupsSidebarItem
            singularTitle="My Group"
            pluralTitle="My Groups"
            icon={GroupIcon}
          />
          <SidebarItem icon={AccountTreeIcon} to="catalog-graph" text="Graph" />
          <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          <SidebarItem icon={OfflineBoltIcon} to="olympus" text="Olympus" />
          <SidebarItem icon={AddTaskIcon} to="olympus-start" text="Olympus Start" />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Build" icon={<CreateComponentIcon />}>
          <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
          <SidebarItem icon={CloudIcon} to="catalog-import" text="Register" />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarScrollWrapper>
          {isAdminsViewAllowed && (
            <SidebarItem
              icon={SettingsApplications}
              to="administration"
              text="Admin"
            />
          )}
        </SidebarScrollWrapper>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
